import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';


const EquipmentStep = ({ data, onUpdate, onNext, onBack }) => {
  const [selectedEquipment, setSelectedEquipment] = useState(data?.equipment || null);
  const [error, setError] = useState('');

  const roomEquipments = {
    'cuisine': [
      { id: 'evier', name: 'Évier', icon: 'Droplet', description: 'Bouchon, fuite, robinet' },
      { id: 'lave-vaisselle', name: 'Lave-vaisselle', icon: 'Waves', description: 'Fuite, évacuation, arrivée d\'eau' },
      { id: 'robinet-cuisine', name: 'Robinet', icon: 'Droplet', description: 'Fuite, pression, mitigeur' },
      { id: 'tuyauterie-cuisine', name: 'Tuyauterie', icon: 'Pipe', description: 'Fuite, raccordement' }
    ],
    'salle-de-bains': [
      { id: 'lavabo', name: 'Lavabo', icon: 'Droplet', description: 'Bouchon, fuite, siphon' },
      { id: 'douche', name: 'Douche', icon: 'Droplet', description: 'Fuite, évacuation, mitigeur' },
      { id: 'baignoire', name: 'Baignoire', icon: 'Droplet', description: 'Fuite, évacuation, robinetterie' },
      { id: 'robinet-sdb', name: 'Robinet', icon: 'Droplet', description: 'Fuite, pression, mitigeur' }
    ],
    'wc': [
      { id: 'cuvette', name: 'Cuvette WC', icon: 'Home', description: 'Fuite, fixation, joint' },
      { id: 'chasse-eau', name: 'Chasse d\'eau', icon: 'Waves', description: 'Fuite, mécanisme, flotteur' },
      { id: 'reservoir', name: 'Réservoir', icon: 'Box', description: 'Fuite, remplissage' },
      { id: 'evacuation-wc', name: 'Évacuation', icon: 'Pipe', description: 'Bouchon, fuite' }
    ],
    'buanderie': [
      { id: 'lave-linge', name: 'Lave-linge', icon: 'Waves', description: 'Fuite, arrivée d\'eau, évacuation' },
      { id: 'evier-buanderie', name: 'Évier', icon: 'Droplet', description: 'Bouchon, fuite' },
      { id: 'tuyauterie-buanderie', name: 'Tuyauterie', icon: 'Pipe', description: 'Fuite, raccordement' }
    ],
    'sous-sol': [
      { id: 'chaudiere', name: 'Chaudière', icon: 'Flame', description: 'Fuite, pression, dysfonctionnement' },
      { id: 'ballon-eau', name: 'Ballon d\'eau chaude', icon: 'Flame', description: 'Fuite, température, résistance' },
      { id: 'compteur', name: 'Compteur d\'eau', icon: 'Gauge', description: 'Fuite, vanne, raccordement' },
      { id: 'tuyauterie-generale', name: 'Tuyauterie générale', icon: 'Pipe', description: 'Fuite, gel, corrosion' }
    ],
    'exterieur': [
      { id: 'robinet-exterieur', name: 'Robinet extérieur', icon: 'Droplet', description: 'Fuite, gel, vanne' },
      { id: 'arrivee-eau', name: 'Arrivée d\'eau', icon: 'Pipe', description: 'Fuite, pression, vanne' },
      { id: 'evacuation-ext', name: 'Évacuation', icon: 'Pipe', description: 'Bouchon, fuite, regard' },
      { id: 'fosse', name: 'Fosse septique', icon: 'AlertCircle', description: 'Bouchon, odeur, débordement' }
    ],
    'autre': [
      { id: 'radiateur', name: 'Radiateur', icon: 'Flame', description: 'Fuite, purge, vanne' },
      { id: 'tuyauterie-autre', name: 'Tuyauterie', icon: 'Pipe', description: 'Fuite, raccordement' },
      { id: 'autre-equipement', name: 'Autre équipement', icon: 'HelpCircle', description: 'À préciser' }
    ]
  };

  const selectedRoom = data?.room;
  const equipments = selectedRoom ? roomEquipments?.[selectedRoom?.id] || [] : [];

  useEffect(() => {
    // Reset equipment selection if room changed
    if (data?.equipment && selectedRoom && data?.equipment?.roomId !== selectedRoom?.id) {
      setSelectedEquipment(null);
    }
  }, [selectedRoom, data?.equipment]);

  const handleEquipmentSelect = (equipment) => {
    setSelectedEquipment({ ...equipment, roomId: selectedRoom?.id });
    setError('');
  };

  const handleSubmit = () => {
    if (!selectedEquipment) {
      setError('Veuillez sélectionner l\'équipement concerné');
      return;
    }

    onUpdate({ equipment: selectedEquipment });
    onNext(true);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Wrench" size={24} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-semibold text-foreground mb-2">Quel équipement est concerné ?</h2>
            <p className="text-muted-foreground">
              Sélectionnez l'équipement spécifique dans {selectedRoom?.name}
            </p>
          </div>
        </div>

        {/* Room Context Banner */}
        <div className="mb-6 bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Icon name={selectedRoom?.icon} size={20} className="text-primary" />
            <div>
              <p className="text-sm font-medium text-foreground">
                Pièce sélectionnée : <span className="text-primary">{selectedRoom?.name}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {selectedRoom?.description}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {equipments?.map((equipment) => (
            <button
              key={equipment?.id}
              onClick={() => handleEquipmentSelect(equipment)}
              className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                selectedEquipment?.id === equipment?.id
                  ? 'border-primary bg-primary/5 shadow-md'
                  : 'border-border hover:border-primary/50 bg-card'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedEquipment?.id === equipment?.id ? 'bg-primary/20' : 'bg-muted'
                    }`}
                  >
                    <Icon
                      name={equipment?.icon}
                      size={20}
                      color={selectedEquipment?.id === equipment?.id ? 'var(--color-primary)' : 'currentColor'}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm text-foreground">{equipment?.name}</h3>
                  </div>
                </div>
                {selectedEquipment?.id === equipment?.id && (
                  <Icon name="CheckCircle" size={20} color="var(--color-primary)" />
                )}
              </div>
              <p className="text-xs text-muted-foreground ml-13">{equipment?.description}</p>
            </button>
          ))}
        </div>

        {error && (
          <div className="mt-6 flex items-center gap-2 text-sm text-error bg-error/5 border border-error/20 rounded-lg p-4">
            <Icon name="AlertCircle" size={16} />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t border-border">
          <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
            Retour
          </Button>
          <Button
            variant="default"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={handleSubmit}
          >
            Continuer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EquipmentStep;